import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/axios/api'

import { AxiosErrorHandler } from '@models/helpers/axiosErrorHandler'
import {
  LanguageList,
  type LanguageType,
  type LanguageInfoType,
  type DictionaryType
} from '@models/entities/languages'
import {
  LocalizationRequestPendings,
  type LocalizationRequestPendingType,
  type LocalizationRequestPendingsType
} from '@models/requests/pendings'

import { csvToArray } from '@scripts/modules/parseCsv'
import { isAvailableLocalStorage } from '@scripts/helpers/windowHelpers'
import { createLanguageTranslationsRequestUrl } from '@scripts/helpers/appHelpers'

import { LANGUAGE_MAP } from '@definitions/languages'

export const useLocalizationStore = defineStore('useLocalizationStore', () => {
  const isReceivedAvailableLanguages = ref(import.meta.env.MODE ==='development')
  const isReceivedDefaultLanguageTranslations = ref(import.meta.env.MODE ==='development')
  const isReceivedCountriesTranslations = ref(import.meta.env.MODE ==='development')

  const _appLanguage = ref({
    available: [] as LanguageType[],
    value: LanguageList.EN as LanguageType
  })
  const _requestPendings = ref<LocalizationRequestPendingsType>({
    [LocalizationRequestPendings.GET_DEFAULT_LANGUAGE_TRANSLATIONS_PENDING]: false,
    [LocalizationRequestPendings.GET_COUNTRIES_LANGUAGE_TRANSLATIONS_PENDING]: false
  })

  const translations = ref<DictionaryType>({} as DictionaryType)
  const countriesTranslations = ref<DictionaryType>({} as DictionaryType)

  const getAppLanguage = computed<LanguageType>(() => _appLanguage.value.value)
  const getAppLanguageShortName = computed(() => LANGUAGE_MAP[getAppLanguage.value].shortName)
  const getAvailableLanguages = computed<LanguageType[]>(() => _appLanguage.value.available)
  const getAllAvailableLanguages = computed<LanguageType[]>(() =>
    [...new Set([getAppLanguage.value, ...getAvailableLanguages.value])])
  const getAllAvailableLanguagesInfo = computed<LanguageInfoType[]>(() => getAllAvailableLanguages.value
    .map((languageKey: LanguageType) => LANGUAGE_MAP[languageKey])
    .sort((a, b) => a.shortName.localeCompare(b.shortName)))
  const getTranslatedLanguages = computed<LanguageType[]>(() => Object.keys(translations.value) as LanguageType[])

  const _setReceivedAvailableLanguages = (status: boolean = true) => {
    isReceivedAvailableLanguages.value = status
  }

  const _setAvailableLanguages = (languages: LanguageType[]) => {
    _appLanguage.value.available = languages
  }

  const _setTranslations = (dictionary: DictionaryType) => {
    translations.value = dictionary
  }

  const _setCountriesTranslations = (dictionary: DictionaryType) => {
    countriesTranslations.value = dictionary
  }

  const setAppLanguage = (language: LanguageType, setToLocalStorage: boolean = false) => {
    _appLanguage.value.value = language

    if (setToLocalStorage && isAvailableLocalStorage()) {
      localStorage.setItem('lang', language)
    }
  }

  const _parseCsv = (csvData: string): string[][] => csvToArray(csvData)

  const _prepareDictionary = (parsedCsvData: string[][]): DictionaryType => {
    const languageKeys = parsedCsvData.shift()! as LanguageType[]

    return languageKeys.reduce((dict, lang, idx) => {
      dict[lang] = parsedCsvData.reduce((acc, cur) => {
        acc[cur[0]] = cur[idx + 1]
        return acc
      }, {} as Record<string, string>)

      return dict
    }, {} as DictionaryType)
  }

  const _setLanguageTranslations = (csvData: string) => {
    _setTranslations({
      ...translations.value,
      ..._prepareDictionary(_parseCsv(csvData))
    })
  }

  const _setCountriesLanguageTranslations = (csvData: string) => {
    _setCountriesTranslations({
      ...countriesTranslations.value,
      ..._prepareDictionary(_parseCsv(csvData))
    })
  }

  const _setRequestPending = (requestPending: LocalizationRequestPendingType, status: boolean = true) => {
    _requestPendings.value[requestPending] = status
  }

  const _getAvailableLanguagesTranslations = (url: string) => {
    api.get(url)
      .then((response) => {
        _setLanguageTranslations(response.data)
      })
      .catch((error) => {
        new AxiosErrorHandler(error)
      })
  }

  const getDefaultLanguageTranslations = async (url: string) => {
    await api.get(url)
      .then((response) => {
        _setLanguageTranslations(response.data)
        isReceivedDefaultLanguageTranslations.value = true
      })
      .catch((error) => {
        console.error(error)

        if (import.meta.env.MODE !=='development' && !_requestPendings.value[LocalizationRequestPendings.GET_DEFAULT_LANGUAGE_TRANSLATIONS_PENDING]) {
          _setRequestPending(LocalizationRequestPendings.GET_DEFAULT_LANGUAGE_TRANSLATIONS_PENDING)

          setTimeout(() => {
            _setRequestPending(LocalizationRequestPendings.GET_DEFAULT_LANGUAGE_TRANSLATIONS_PENDING, false)
            getDefaultLanguageTranslations(url)
          }, 5000)
        }
      })
  }

  const getLanguages = async () => {
    await api.get('/personal-api/v1/langs/')
      .then((response) => {
        _setAvailableLanguages(response.data)
        _setReceivedAvailableLanguages()
      })
      .catch((error) => {
        new AxiosErrorHandler(error)
      })
  }

  const getAllAvailableLanguagesTranslations = () => {
    getAvailableLanguages.value.forEach((language: LanguageType) => {
      if (language !== getAppLanguage.value) {
        const url = createLanguageTranslationsRequestUrl(language)
        _getAvailableLanguagesTranslations(url)
      }
    })
  }

  const getCountriesTranslations = () => {
    api.get(`https://${import.meta.env.VITE_APP_DOMAIN}/files/langs/countries.csv`)
      .then(response => {
        _setCountriesLanguageTranslations(response.data)
        isReceivedCountriesTranslations.value = true
      })
      .catch(error => {
        console.error(error)

        if (import.meta.env.MODE !== 'development' && !_requestPendings.value[LocalizationRequestPendings.GET_COUNTRIES_LANGUAGE_TRANSLATIONS_PENDING]) {
          _setRequestPending(LocalizationRequestPendings.GET_COUNTRIES_LANGUAGE_TRANSLATIONS_PENDING)

          setTimeout(() => {
            _setRequestPending(LocalizationRequestPendings.GET_DEFAULT_LANGUAGE_TRANSLATIONS_PENDING, false)
            getCountriesTranslations()
          }, 5000)
        }
      })
  }

  return {
    isReceivedAvailableLanguages,
    isReceivedDefaultLanguageTranslations,
    isReceivedCountriesTranslations,
    translations,
    countriesTranslations,
    getAppLanguage,
    getAppLanguageShortName,
    getAvailableLanguages,
    getAllAvailableLanguages,
    getAllAvailableLanguagesInfo,
    getTranslatedLanguages,
    setAppLanguage,
    getDefaultLanguageTranslations,
    getLanguages,
    getAllAvailableLanguagesTranslations,
    getCountriesTranslations
  }
})
